/**
 * 🌞 LIGHT THEME
 *
 * Стандартная светлая тема - базовый набор цветов.
 * Полностью самодостаточна, содержит все цвета напрямую.
 *
 * Архитектура: все цвета определены непосредственно в теме,
 * без ссылок на внешние примитивы или другие файлы.
 */

import type { ThemeTokens } from '../semantic';
import { createThemeWithDerivations } from '../color-derivations';

const baseLightTheme = {
  // ============================================
  // 📐 SURFACES (поверхности)
  // ============================================
  bg: '#EEF1F5',                          // Чуть темнее и холоднее базовый фон приложения
  surface: '#FCFCFD',                     // Чуть светлее средняя поверхность страницы
  card: '#FFFFFF',                        // Чисто белый
  elevated: '#FFFFFF',                    // Чисто белый (приподнятые элементы)

  // ============================================
  // 📝 TEXT (цвета текста для светлой темы)
  // ============================================
  text: '#1F2328',                        // Темно-серый основной текст
  textMuted: '#5B616A',                   // Средне-серый вторичный текст
  textTertiary: '#667179',                // Третичный текст (контраст ~4.50 на bg, ~4.79 на surface)
  textPlaceholder: '#868A92',             // Плейсхолдеры (контраст ~3.12 на bg, читаемый)

  // ============================================
  // 🟡 PRIMARY (основной акцент)
  // ============================================
  primary: '#FFC107',                     // Желтый акцент
  onPrimary: '#1B1B1B',                   // Темный текст на желтом
  link: '#876500',                        // Янтарный для ссылок/иконок (контраст ~4.86 на bg, ~5.40 на white)
  onLightSurface: '#1B1B1B',              // Темный текст на светлых плашках (статусы, карточки)

  // ============================================
  // 📊 BORDERS (границы)
  // ============================================
  border: '#D7DCE4',                      // Мягкие светло-серые границы
  borderStrong: '#C2CAD6',                // Средние серые границы (не совпадает с disabled)

  // ============================================
  // ✅ SEMANTIC COLORS (семантические цвета)
  // ============================================
  success: '#4E8A67',
  onSuccess: '#0B0B0B',                   // Контрастный черный для WCAG AA
  warning: '#B9823A',
  onWarning: '#0B0B0B',                   // Контрастный черный для WCAG AA
  error: '#C85C5C',
  onError: '#0B0B0B',                     // Контрастный черный для WCAG AA
  info: '#4E7FA8',
  onInfo: '#0B0B0B',                      // Контрастный черный для WCAG AA

  // ============================================
  // 🎯 INTERACTIVE (интерактивные состояния)
  // ============================================
  focus: 'rgba(255, 193, 7, 0.22)',        // Желтое кольцо фокуса
  disabled: '#898D91',                     // Отключенный текст (контраст ~3.01 на bg, читается)

  // ============================================
  // 🌑 SCRIM (затемнение)
  // ============================================
  scrim: 'rgba(0, 0, 0, 0.05)',             // Заблюренный чёрный 5%

  // ============================================
  // 📜 SCROLLBAR (полоса прокрутки графика аналитики)
  // ============================================
  scrollbarTrack: '#FAFAFA',   // surface
  scrollbarThumb: '#D7DCE4',   // border
  scrollbarThumbHover: '#5B616A', // textMuted

  // ============================================
  // 📅 APPOINTMENT STATUSES (статусы записей)
  // ============================================
  apptScheduledBg: '#BBDEFB', // Blue 100
  apptCompletedBg: '#DCEDC8', // Light Green 100 (теплее)
  apptNoShowBg: '#FFE0B2', // Orange 100 (более оранжевый)
  apptCancelledBg: '#FFCDD2', // Red 100
  apptMovedBg: '#E1BEE7', // Purple 100
  apptBreakBg: '#CFD8DC', // Blue Grey 100
};

export const lightTheme: ThemeTokens = createThemeWithDerivations(baseLightTheme, 'light');
